import React, { useEffect, useState, Suspense, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { ProjectProvider, useProjects } from './contexts/ProjectContext';
import { PostHogProvider } from './contexts/PostHogContext';
import AuthForm from './components/AuthForm';
import PublicHome from './components/PublicHome';
import UnifiedHeader from './components/UnifiedHeader';
import AppLayout from './components/AppLayout';
import ErrorBoundary from './components/ErrorBoundary';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import DashboardErrorBoundary from './components/DashboardErrorBoundary';
import { GlobalLoadingIndicator } from './components/LoadingStates';
import LoadingSpinner from './components/LoadingSpinner';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';
// Static data pages, small enough that a separate chunk buys nothing and costs
// a network round trip the prerendered HTML can fail on.
import PartnersPage from './components/PartnersPage';
import ProjectsShowcasePage from './components/ProjectsShowcasePage';
import { isOnPremDeployment, getCoreApiUrl } from './config/deploymentConfig';
import { lazyWithRetry } from './utils/lazyWithRetry';
import './App.css';

// Environment flag for enabling public homepage
const ENABLE_PUBLIC_HOMEPAGE = process.env.REACT_APP_ENABLE_PUBLIC_HOMEPAGE === 'true';

// Lazy load heavy components. lazyWithRetry (not bare React.lazy) so a chunk
// URL retired by a deploy recovers instead of dropping the visitor on an error
// page — see webapp/src/utils/lazyWithRetry.js.
const ProjectSelector = lazyWithRetry(() => import('./ProjectSelector'));
const Dashboard = lazyWithRetry(() => import('./Dashboard'));
const SymbolsView = lazyWithRetry(() => import('./components/SymbolsView'));
const BuildComparePage = lazyWithRetry(() => import('./components/BuildComparePage'));
const ProjectSettings = lazyWithRetry(() => import('./components/ProjectSettings'));
const RegionConfigPage = lazyWithRetry(() => import('./components/RegionConfigPage'));
const BudgetsPage = lazyWithRetry(() => import('./components/BudgetsPage'));
const PricingPage = lazyWithRetry(() => import('./components/PricingPage'));
const AccountSettings = lazyWithRetry(() => import('./components/AccountSettings'));
const DemoPage = lazyWithRetry(() => import('./components/DemoPage'));
const AboutPage = lazyWithRetry(() => import('./components/AboutPage'));
const TermsPage = lazyWithRetry(() => import('./components/TermsPage'));
const TermsOSSPage = lazyWithRetry(() => import('./components/TermsOSSPage'));
const PrivacyPage = lazyWithRetry(() => import('./components/PrivacyPage'));
const ProjectPage = lazyWithRetry(() => import('./components/ProjectPage'));
const BlogList = lazyWithRetry(() => import('./components/BlogList'));
const BlogArticle = lazyWithRetry(() => import('./components/BlogArticle'));
const AdminPage = lazyWithRetry(() => import('./components/AdminPage'));

// Main app content component that uses ProjectContext
const AppContent = () => {
  const { user, loading, signOut } = useAuth();
  const {
    fetchProjects,
    loading: { targets: targetsLoading, heads: headsLoading, memoryReports: memoryReportsLoading }
  } = useProjects();

  // License status for on-prem deployments
  const [licenseExpired, setLicenseExpired] = useState(false);

  // Determine if any background operations are running
  const hasBackgroundLoading = Object.values(targetsLoading).some(Boolean) ||
                               Object.values(headsLoading).some(Boolean) ||
                               Object.values(memoryReportsLoading).some(Boolean);

  // Track if we've already handled the OAuth redirect to prevent infinite loops
  const hasHandledRedirectRef = useRef(false);

  // Check license status for on-prem deployments
  useEffect(() => {
    if (!isOnPremDeployment()) return;

    const checkLicense = async () => {
      try {
        const baseUrl = getCoreApiUrl();
        const response = await fetch(`${baseUrl}/health`);
        if (response.ok) {
          const data = await response.json();
          if (data.license) {
            setLicenseExpired(!data.license.valid || data.license.expired);
          }
        }
      } catch (err) {
        // Silently fail - license banner is non-critical
      }
    };

    checkLicense();
  }, []);

  // Handle post-OAuth redirect from sessionStorage
  // Only runs for OAuth flows to avoid race condition with email/password login
  useEffect(() => {
    if (user && !hasHandledRedirectRef.current) {
      // Check if this is an OAuth flow by detecting Supabase OAuth hash fragments
      const isOAuthFlow = window.location.hash.includes('access_token') ||
                          window.location.hash.includes('error');

      if (isOAuthFlow) {
        const redirectPath = sessionStorage.getItem('redirectAfterLogin');
        hasHandledRedirectRef.current = true;

        if (redirectPath) {
          sessionStorage.removeItem('redirectAfterLogin');
          // Use window.location to ensure we're in the correct Router context
          window.location.href = redirectPath;
        } else {
          // OAuth from direct login page (no saved redirect path)
          window.location.href = '/projects';
        }
      }
    }
  }, [user]);

  // Conditional project loading - skip for symbols page optimization
  useEffect(() => {
    if (user) {
      // Only load projects if not on symbols page (use window.location for now)
      const isSymbolsPage = window.location.pathname.includes('/symbols');
      if (!isSymbolsPage) {
        // A failure lands in errors.projects; ProjectSelector renders it with a Retry button
        fetchProjects().catch(() => {});
      }
    }
  }, [user, fetchProjects]);

  const handleSignOut = async () => {
    try {
      await signOut();
      // Navigation handled by ProtectedRoute when user becomes null
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error signing out:', error);
    }
  };

  // Show loading screen while checking authentication
  // Note: Don't check projectsLoading here to avoid unmounting the Router
  // Each component handles its own project loading state
  if (loading) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner"></div>
        <div className="app-loading-text">Loading...</div>
      </div>
    );
  }

  // Unified routing with protected routes
  return (
    <Router>
      <ScrollToTop />
      <PostHogProvider>
        <UnifiedRoutes
          user={user}
          signOut={handleSignOut}
          hasBackgroundLoading={hasBackgroundLoading}
          licenseExpired={licenseExpired}
        />
      </PostHogProvider>
    </Router>
  );
};

// Unified routes component that handles both public and protected routes
const UnifiedRoutes = ({ user, signOut, hasBackgroundLoading, licenseExpired }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigateHome = () => {
    // Always navigate to landing page (/)
    // If public homepage is disabled and user is authenticated, they'll be redirected to /projects
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const handleNavigateToAbout = () => {
    if (location.pathname === '/about') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/about');
    }
  };

  const handleNavigateToPartners = () => {
    if (location.pathname === '/partners') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/partners');
    }
  };

  const handleNavigateToPricing = () => {
    if (location.pathname === '/pricing') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/pricing');
    }
  };

  const handleNavigateToDemo = () => {
    if (location.pathname === '/demo') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/demo');
    }
  };

  const handleNavigateToBlog = () => {
    if (location.pathname === '/blog') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/blog');
    }
  };

  const handleNavigateToShowcase = () => {
    if (location.pathname === '/showcase') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/showcase');
    }
  };

  const handleNavigateToAuth = () => {
    if (location.pathname === '/signup') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/signup');
    }
  };

  const handleNavigateToSettings = () => {
    if (location.pathname === '/settings') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/settings');
    }
  };

  // For unauthenticated users without public homepage, show login form
  // But allow access to public project routes (/public/...) and legal pages
  const isPublicProjectRoute = location.pathname.startsWith('/public/');
  const isLegalRoute = ['/terms', '/terms-oss', '/privacy'].includes(location.pathname);
  if (!user && !ENABLE_PUBLIC_HOMEPAGE && !isPublicProjectRoute && !isLegalRoute) {
    return (
      <div className="app-container">
        <UnifiedHeader
          user={null}
          onNavigateHome={() => navigate('/')}
          onNavigateToAuth={handleNavigateToAuth}
          showMarketingPages={false}
          licenseExpired={licenseExpired}
        />
        <div className="app-content">
          <AuthForm mode="login" />
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <GlobalLoadingIndicator isVisible={hasBackgroundLoading} />

      <UnifiedHeader
        user={user}
        onSignOut={signOut}
        onNavigateHome={handleNavigateHome}
        onNavigateToAbout={handleNavigateToAbout}
        onNavigateToPartners={handleNavigateToPartners}
        onNavigateToPricing={handleNavigateToPricing}
        onNavigateToDemo={handleNavigateToDemo}
        onNavigateToBlog={handleNavigateToBlog}
        onNavigateToShowcase={handleNavigateToShowcase}
        onNavigateToAuth={handleNavigateToAuth}
        onNavigateToSettings={handleNavigateToSettings}
        showMarketingPages={ENABLE_PUBLIC_HOMEPAGE}
        licenseExpired={licenseExpired}
      />

      <div className="app-content">
        <RouteErrorBoundary>
            <Routes>
              {/* Public home page when enabled */}
              {ENABLE_PUBLIC_HOMEPAGE && (
                <Route path="/" element={<PublicHome />} />
              )}

              {/* Redirect authenticated users to /projects when public homepage is disabled */}
              {user && !ENABLE_PUBLIC_HOMEPAGE && (
                <Route path="/" element={<Navigate to="/projects" replace />} />
              )}

              {/* Public project page - shows project info and target list */}
              <Route
                path="/public/:githubAuthor/:githubProject"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<LoadingSpinner message="Loading project..." />}>
                      <ProjectPage />
                    </Suspense>
                  </ErrorBoundary>
                }
              />

              {/* Public project target routes - no authentication required, wrapped in AppLayout */}
              <Route path="/public/:githubAuthor/:githubProject/target/:targetName" element={<AppLayout />}>
                <Route
                  index
                  element={
                    <DashboardErrorBoundary>
                      <Suspense fallback={<LoadingSpinner message="Loading public project..." />}>
                        <Dashboard />
                      </Suspense>
                    </DashboardErrorBoundary>
                  }
                />
                <Route
                  path="symbols"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingSpinner message="Loading symbols analysis..." />}>
                        <SymbolsView />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="compare"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingSpinner message="Loading build comparison..." />}>
                        <BuildComparePage />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="region-config"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingSpinner message="Loading region configuration..." />}>
                        <RegionConfigPage />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="budgets"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingSpinner message="Loading budgets..." />}>
                        <BudgetsPage />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
              </Route>

              {/* Protected routes - all project-related routes use AppLayout with nested children */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route
                  path="projects"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingSpinner message="Loading projects..." />}>
                        <ProjectSelector />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="project/:projectId"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingSpinner message="Loading project..." />}>
                        <ProjectPage />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="project/:projectId/target/:targetName"
                  element={
                    <DashboardErrorBoundary>
                      <Suspense fallback={<LoadingSpinner message="Loading dashboard..." />}>
                        <Dashboard />
                      </Suspense>
                    </DashboardErrorBoundary>
                  }
                />
                <Route
                  path="project/:projectId/target/:targetName/symbols"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingSpinner message="Loading symbols analysis..." />}>
                        <SymbolsView />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="project/:projectId/target/:targetName/compare"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingSpinner message="Loading build comparison..." />}>
                        <BuildComparePage />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="project/:projectId/settings"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingSpinner message="Loading project settings..." />}>
                        <ProjectSettings />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="project/:projectId/target/:targetName/region-config"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingSpinner message="Loading region configuration..." />}>
                        <RegionConfigPage />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="project/:projectId/target/:targetName/budgets"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingSpinner message="Loading budgets..." />}>
                        <BudgetsPage />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingSpinner message="Loading account settings..." />}>
                        <AccountSettings />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="admin"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingSpinner message="Loading admin dashboard..." />}>
                        <AdminPage />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
              </Route>

              {/* Public authentication routes */}
              <Route path="/signin" element={<AuthForm mode="login" />} />
              <Route path="/signup" element={<AuthForm mode="register" />} />

              {ENABLE_PUBLIC_HOMEPAGE && (
                <>
                  <Route path="/about" element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingSpinner message="Loading about..." />}>
                        <AboutPage />
                      </Suspense>
                    </ErrorBoundary>
                  } />
                  <Route path="/partners" element={
                    <ErrorBoundary>
                      <PartnersPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/pricing" element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingSpinner message="Loading pricing..." />}>
                        <PricingPage />
                      </Suspense>
                    </ErrorBoundary>
                  } />
                  <Route path="/demo" element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingSpinner message="Loading demo page..." />}>
                        <DemoPage />
                      </Suspense>
                    </ErrorBoundary>
                  } />
                  <Route path="/blog" element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingSpinner message="Loading blog..." />}>
                        <BlogList />
                      </Suspense>
                    </ErrorBoundary>
                  } />
                  <Route path="/blog/:slug" element={
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingSpinner message="Loading article..." />}>
                        <BlogArticle />
                      </Suspense>
                    </ErrorBoundary>
                  } />
                  <Route path="/showcase" element={
                    <ErrorBoundary>
                      <ProjectsShowcasePage />
                    </ErrorBoundary>
                  } />
                </>
              )}

              {/* Legal pages - always accessible */}
              <Route path="/terms" element={
                <ErrorBoundary>
                  <Suspense fallback={<LoadingSpinner message="Loading terms..." />}>
                    <TermsPage />
                  </Suspense>
                </ErrorBoundary>
              } />
              <Route path="/terms-oss" element={
                <ErrorBoundary>
                  <Suspense fallback={<LoadingSpinner message="Loading terms..." />}>
                    <TermsOSSPage />
                  </Suspense>
                </ErrorBoundary>
              } />
              <Route path="/privacy" element={
                <ErrorBoundary>
                  <Suspense fallback={<LoadingSpinner message="Loading privacy policy..." />}>
                    <PrivacyPage />
                  </Suspense>
                </ErrorBoundary>
              } />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </RouteErrorBoundary>
      </div>
    </div>
  );
};

// Root App component with ProjectProvider
const App = () => {
  return (
    <ErrorBoundary>
      <ProjectProvider>
        <AppContent />
      </ProjectProvider>
    </ErrorBoundary>
  );
};

export default App; 